import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, CheckCircle, AlertCircle, Download } from 'lucide-react'
import { useMutation } from 'react-query'
import { rncpService } from '../../services/rncp'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'

export default function DataImport({ onImportSuccess }) {
  const [importResult, setImportResult] = useState(null)

  const importMutation = useMutation(
    (file) => rncpService.importCsvData(file),
    {
      onSuccess: (data) => {
        setImportResult(data)
        toast.success('Import réussi !')
        if (onImportSuccess) {
          onImportSuccess(data)
        }
      },
      onError: (error) => {
        toast.error(error.message || 'Erreur lors de l\'import')
      }
    }
  )

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      importMutation.mutate(file)
    }
  }, [importMutation])

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/json': ['.json']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  })

  const downloadTemplate = () => {
    const templateData = [
      {
        rncp_code: 'RNCP34947',
        titre_certification: 'CAP - Equipier polyvalent du commerce',
        niveau: 'III',
        secteur: 'Commerce',
        bloc_id: 'BLOC_0001',
        titre_bloc: 'Recevoir et suivre les commandes',
        ordre_bloc: 1,
        competence_id: 'COMP_00001',
        description_competence: 'Participer à la passation des commandes fournisseurs',
        ordre_competence: 1,
        RNCP_TITRE: 'CAP - Equipier polyvalent du commerce'
      }
    ]

    const headers = Object.keys(templateData[0])
    const csvContent = [
      headers.join(','),
      ...templateData.map(row =>
        headers.map(header => {
          let cell = row[header] || ''
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            cell = '"' + cell.replace(/"/g, '""') + '"'
          }
          return cell
        }).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = 'template_rncp.csv'
    link.click()
    URL.revokeObjectURL(link.href)
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Import des données RNCP
        </h3>
        <p className="text-gray-600">
          Importez vos données de certifications RNCP au format CSV, Excel ou JSON
        </p>
      </div>

      {/* Zone de dépôt */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-gray-300 hover:border-gray-400'
          }
          ${importMutation.isLoading ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input {...getInputProps()} />

        {importMutation.isLoading ? (
          <div className="space-y-4">
            <LoadingSpinner size="lg" className="mx-auto" />
            <div>
              <div className="text-lg font-medium text-gray-900">Import en cours...</div>
              <div className="text-gray-600">Traitement des données</div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div>
              <div className="text-lg font-medium text-gray-900">
                {isDragActive ? 'Déposez le fichier ici' : 'Glissez votre fichier ici'}
              </div>
              <div className="text-gray-600 mt-1">
                ou cliquez pour sélectionner un fichier
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Formats supportés: CSV, Excel (.xlsx, .xls), JSON (max 10MB)
            </div>
          </div>
        )}
      </div>

      {/* Fichier sélectionné */}
      {acceptedFiles.length > 0 && !importMutation.isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <FileText className="h-5 w-5 text-blue-600 mr-2" />
            <span className="text-blue-900 font-medium">{acceptedFiles[0].name}</span>
            <span className="text-blue-600 ml-2">
              ({(acceptedFiles[0].size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
        </div>
      )}

      {/* Résultat de l'import */}
      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-green-900 font-medium mb-2">Import réussi !</h4>
              <div className="text-green-800 text-sm space-y-1">
                <div>
                  <strong>{importResult.data.recordsProcessed}</strong> enregistrements traités
                </div>
                <div>
                  <strong>{importResult.data.certificationsCreated}</strong> certifications créées
                </div>
                <div>
                  <strong>{importResult.data.blocksCreated}</strong> blocs de compétences créés
                </div>
                <div>
                  <strong>{importResult.data.competencesCreated}</strong> compétences créées
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between items-center pt-4 border-t">
        <button
          onClick={downloadTemplate}
          className="btn btn-outline"
          type="button"
        >
          <Download className="h-4 w-4 mr-2" />
          Télécharger le modèle CSV
        </button>

        <div className="text-sm text-gray-500">
          <a
            href="https://www.francecompetences.fr/recherche/rncp/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-600 hover:text-emerald-700 underline"
          >
            Consulter le répertoire RNCP officiel
          </a>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">
          Format attendu pour l'import CSV
        </h4>
        <div className="text-sm text-gray-600 space-y-1">
          <div><strong>Colonnes requises :</strong></div>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code className="bg-gray-200 px-1 rounded">rncp_code</code> - Code RNCP (ex: RNCP34947)</li>
            <li><code className="bg-gray-200 px-1 rounded">titre_certification</code> - Nom de la certification</li>
            <li><code className="bg-gray-200 px-1 rounded">description_competence</code> - Description de la compétence</li>
          </ul>
          <div className="mt-2"><strong>Colonnes optionnelles :</strong></div>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li><code className="bg-gray-200 px-1 rounded">niveau</code>, <code className="bg-gray-200 px-1 rounded">secteur</code>, <code className="bg-gray-200 px-1 rounded">bloc_id</code>, <code className="bg-gray-200 px-1 rounded">titre_bloc</code>, etc.</li>
          </ul>
        </div>
      </div>
    </div>
  )
}